import { Box, Flex, IconButton, Spinner, Text } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { LuMaximize } from 'react-icons/lu'
import { CrowdsLogo } from '../components/CrowdsLogo'
import { DownloadButton } from '../components/ui/DownloadButton'
import { ShareButton } from '../components/ui/ShareButton'
import { Tooltip } from '../components/ui/tooltip'
import { getSharedVideo } from '../services/firebaseService'
import { StreamPlayer } from '../components/ui/StreamPlayer'
import { useTranslation } from '../i18n'

export default function SharedVideoPage() {
    const { shareId } = useParams()
    const { t } = useTranslation()
    const [shared, setShared] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    // The player is an iframe, so fullscreen is requested on its wrapper.
    const playerRef = useRef(null)

    useEffect(() => {
        getSharedVideo(shareId)
            .then((data) => {
                if (!data) setError(t('shared.notFound'))
                // The reel was pulled. Its document is kept as a tombstone
                // precisely so this page can say the video is gone rather than
                // that the link is wrong -- and it carries no `cf`, so this
                // has to be asked before the playback check below.
                else if (data.disabled === true) setError(t('shared.unavailable'))
                // Links are only ever minted once the Stream copy is ready, so a
                // doc without one predates that rule and has nothing to play.
                else if (!data.cf?.uid) setError(t('shared.streamUnavailable'))
                else setShared(data)
            })
            .catch(() => setError(t('shared.loadFailed')))
            .finally(() => setLoading(false))
    }, [shareId, t])

    const handleFullscreen = () => {
        const el = playerRef.current
        if (!el) return
        if (el.requestFullscreen) el.requestFullscreen()
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen()
    }

    if (loading) {
        return (
            <Flex direction="column" minH="100vh" bg="var(--mc-bg)" align="center" justify="center">
                <Spinner size="xl" color="var(--mc-accent)" />
            </Flex>
        )
    }

    if (error) {
        return (
            <Flex direction="column" minH="100vh" bg="var(--mc-bg)" align="center" justify="center">
                <Text color="var(--mc-steel)" fontSize="lg">{error}</Text>
            </Flex>
        )
    }

    return (
        <Flex direction="column" h="100vh" bg="black" overflow="hidden">
            {/* Header */}
            <Flex
                as="header"
                px={4}
                py={2}
                align="center"
                borderBottom="1px solid"
                borderColor="var(--mc-border)"
                bg="var(--mc-bg-header)"
                flexShrink={0}
            >
                <CrowdsLogo w="120px" h="32px" />
                <Box flex="1" />
                <Text
                    fontSize="sm"
                    color="var(--mc-steel)"
                    mr={3}
                    fontFamily="'DM Sans', sans-serif"
                    display={{ base: 'none', sm: 'block' }}
                    noOfLines={1}
                    maxW="40vw"
                >
                    {shared.title}
                </Text>
                <Tooltip content={t('shared.fullscreen')} openDelay={300} closeDelay={100}>
                    <IconButton
                        size="sm"
                        variant="ghost"
                        color="var(--mc-steel)"
                        _hover={{ color: 'var(--mc-accent)' }}
                        aria-label={t('shared.fullscreen')}
                        onClick={handleFullscreen}
                        mr={1}
                    >
                        <LuMaximize />
                    </IconButton>
                </Tooltip>
                {/* The URL fallback points at this page, not at the storage
                    object: a recipient should land somewhere that plays. */}
                <ShareButton
                    iconOnly
                    source={{ shareId }}
                    cachedDirect={shared.cfDirectUrl}
                    cfUid={shared.cf?.uid}
                    url={window.location.href}
                    title={shared.title}
                    size="sm"
                    variant="ghost"
                    color="var(--mc-steel)"
                    _hover={{ color: 'var(--mc-accent)' }}
                    tooltip={t('shared.shareVideo')}
                    mr={1}
                />
                <DownloadButton
                    iconOnly
                    source={{ shareId }}
                    url={shared.videoUrl}
                    fileName={shared.fileName || shared.title}
                    size="sm"
                    variant="ghost"
                    color="var(--mc-steel)"
                    _hover={{ color: 'var(--mc-accent)' }}
                    tooltip={t('shared.downloadVideo')}
                />
            </Flex>

            {/* Video — fills remaining space */}
            <Flex flex="1" align="center" justify="center" bg="black" minH={0}>
                <Box ref={playerRef} position="relative" width="100%" height="100%" bg="black">
                    <StreamPlayer cf={shared.cf} controls autoPlay fill />
                </Box>
            </Flex>
        </Flex>
    )
}
